import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useScreenUtilsStore = defineStore('screenUtils', () => {
  // ========= 全屏基础 =========
  const width = ref(window.innerWidth)
  const height = ref(window.innerHeight)
  const isRotated = ref(window.innerWidth < window.innerHeight) // 竖屏=旋转

  // 你的 App.vue 是逆时针 -90deg；如果改成顺时针，就设 true
  const ROTATE_CLOCKWISE = false

  function updateSize() {
    width.value = window.innerWidth
    height.value = window.innerHeight
    isRotated.value = width.value < height.value
    if (viewportEl.value) _updateViewportRect()
  }

  // ========= 视口覆盖（中间地图容器） =========
  const viewportEl = ref(null)
  const viewportRect = ref({ left: 0, top: 0, width: window.innerWidth, height: window.innerHeight })
  let _ro = null

  function setViewportEl(el) {
    viewportEl.value = el || null
    if (_ro) { try { _ro.disconnect() } catch {} _ro = null }
    if (viewportEl.value) {
      _updateViewportRect()
      try {
        _ro = new ResizeObserver(() => _updateViewportRect())
        _ro.observe(viewportEl.value)
      } catch {}
    } else {
      _updateViewportRect()
    }
  }
  function clearViewportEl() {
    viewportEl.value = null
    if (_ro) { try { _ro.disconnect() } catch {} _ro = null }
    _updateViewportRect()
  }
  function _updateViewportRect() {
    if (viewportEl.value) {
      const r = viewportEl.value.getBoundingClientRect()
      viewportRect.value = { left: r.left, top: r.top, width: r.width, height: r.height }
    } else {
      viewportRect.value = { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight }
    }
  }

  // —— 备用：视觉 ↔ 真实屏坐标（未旋转）
  function visualToReal(x, y) {
    if (!isRotated.value) return { x, y }
    if (ROTATE_CLOCKWISE) return { x: y, y: width.value - x }
    return { x: height.value - y, y: x }
  }
  function realToVisual(rx, ry) {
    if (!isRotated.value) return { x: rx, y: ry }
    if (ROTATE_CLOCKWISE) return { x: width.value - ry, y: rx }
    return { x: ry, y: height.value - rx }
  }

  // —— 舞台逻辑尺寸：以“视口”尺寸为准
  function getStageSize() {
    const vw = viewportRect.value.width
    const vh = viewportRect.value.height
    return isRotated.value
      ? { stageW: vh, stageH: vw }
      : { stageW: vw, stageH: vh }
  }

  function getStageOffset() {
    const { stageW, stageH } = getStageSize()
    const vw = viewportRect.value.width
    const vh = viewportRect.value.height
    const left = (vw - stageW) / 2
    const top  = (vh - stageH) / 2
    return { left, top }
  }

  /**
   * 视觉(clientX/Y) -> renderer 坐标
   * 先转为“视口内局部坐标”（减去 rect.left/top），再做旋转映射。
   */
  function visualToRenderer(clientX, clientY, rendererW, rendererH) {
    const rect = viewportRect.value
    const vx = clientX - rect.left
    const vy = clientY - rect.top
    const scrW = rect.width
    const scrH = rect.height

    if (!isRotated.value) {
      const sx = vx * (rendererW / scrW)
      const sy = vy * (rendererH / scrH)
      return { sx, sy }
    } else {
      if (ROTATE_CLOCKWISE) {
        const sx = (scrH - vy) * (rendererW / scrH)
        const sy =  vx          * (rendererH / scrW)
        return { sx, sy }
      } else {
        const sx =  vy          * (rendererW / scrH)
        const sy = (scrW - vx)  * (rendererH / scrW)
        return { sx, sy }
      }
    }
  }

  // 视觉坐标 -> 世界(地图)坐标
  function visualToWorld(vx, vy, rendererW, rendererH, offsetX, offsetY, scale) {
    const { sx, sy } = visualToRenderer(vx, vy, rendererW, rendererH)
    return {
      x: (sx - offsetX) / scale,
      y: (sy - offsetY) / scale,
    }
  }

  return {
    width, height, isRotated, updateSize,
    setViewportEl, clearViewportEl,
    visualToReal, realToVisual,
    getStageSize, getStageOffset,
    visualToRenderer, visualToWorld,
  }
})
